(() => {
  const escapeHtml = (value = '') =>
    String(value).replace(/[&<>"']/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    }[character]));

  function initializeHeader(header) {
    if (!header || header.dataset.headerInitialized === 'true') return;
    header.dataset.headerInitialized = 'true';

    /* --- DOM --- */
    const burger = header.querySelector('.header-burger');
    const sidebar = header.querySelector('.mobile-sidebar');
    const overlay = header.querySelector('.mobile-sidebar-overlay');
    const sidebarClose = header.querySelector('.mobile-sidebar__close');
    const submenuItems = header.querySelectorAll('.mobile-nav__item.has-submenu');

    const searchTriggers = header.querySelectorAll('.header-search-trigger');
    const searchPanel = header.querySelector('.header-search-drawer');
    const panelInput = searchPanel?.querySelector('.header-search-modal__input');
    const searchForm = searchPanel?.querySelector('.header-search-modal__form-wrap');
    const closeButtons = searchPanel?.querySelectorAll('[data-search-close]') || [];
    const results = searchPanel?.querySelector('[data-search-results]');
    const viewAll = searchPanel?.querySelector('[data-search-view-all]');
    const searchTags = searchPanel?.querySelectorAll('.search-tag') || [];

    const predictiveUrl = header.dataset.predictiveSearchUrl || '/search/suggest';
    const searchUrl = header.dataset.searchUrl || '/search';

    let controller;
    let debounceTimer;

    /* --- Body scroll lock (shared by drawer + sidebar) --- */
    let lockCount = 0;
    function lockScroll() {
      lockCount += 1;
      document.body.style.overflow = 'hidden';
    }
    function unlockScroll() {
      lockCount = Math.max(0, lockCount - 1);
      if (lockCount === 0) document.body.style.overflow = '';
    }

    /* --- Mobile sidebar --- */
    function openSidebar() {
      if (!sidebar) return;
      sidebar.classList.add('is-open');
      overlay?.classList.add('is-visible');
      sidebar.setAttribute('aria-hidden', 'false');
      burger?.setAttribute('aria-expanded', 'true');
      lockScroll();
      sidebarClose?.focus();
    }

    function closeSidebar() {
      if (!sidebar || !sidebar.classList.contains('is-open')) return;
      sidebar.classList.remove('is-open');
      overlay?.classList.remove('is-visible');
      sidebar.setAttribute('aria-hidden', 'true');
      burger?.setAttribute('aria-expanded', 'false');
      unlockScroll();
      burger?.focus();
    }

    /* --- Mobile submenu accordion --- */
    submenuItems.forEach((item) => {
      const toggleBtn = item.querySelector('.mobile-nav__toggle');
      const toggleIcon = item.querySelector('.mobile-nav__toggle-icon');
      if (!toggleBtn) return;

      toggleBtn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const isExpanded = item.classList.toggle('is-expanded');
        toggleBtn.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
        if (toggleIcon) toggleIcon.textContent = isExpanded ? '−' : '+';
      });
    });

    /* --- Search drawer --- */
    function openSearch(event) {
      if (event) event.preventDefault();
      if (!searchPanel || searchPanel.classList.contains('is-open')) return;

      searchPanel.classList.add('is-open');
      searchPanel.setAttribute('aria-hidden', 'false');
      searchTriggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));
      lockScroll();

      window.setTimeout(() => panelInput?.focus(), 200);

      const query = panelInput?.value.trim() || '';
      if (query) {
        performSearch(query);
      } else {
        resetResults();
      }
    }

    function closeSearch() {
      if (!searchPanel || !searchPanel.classList.contains('is-open')) return;
      searchPanel.classList.remove('is-open');
      searchPanel.setAttribute('aria-hidden', 'true');
      searchTriggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
      unlockScroll();
      searchTriggers[0]?.focus();
    }

    function resetResults() {
      searchPanel?.classList.remove('has-query');
      if (results) results.innerHTML = '';
      if (viewAll) viewAll.hidden = true;
    }

    /* --- Render helpers --- */
    function getImageUrl(item) {
      const image = item.featured_image || item.image;
      if (!image) return '';
      if (typeof image === 'string') return image;
      return image.url || image.src || '';
    }

    // Predictive search returns either "1299.00" (major units) or "129900" (paise).
    function formatMoney(value) {
      if (value === null || value === undefined || value === '') return '';
      const raw = String(value).trim();
      const amount = /[.,]\d{2}$/.test(raw)
        ? Number(raw.replace(/,/g, ''))
        : Number(raw.replace(/[^\d]/g, '')) / 100;
      if (!isFinite(amount)) return '';
      return '₹' + amount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    }

    function renderProductCard(item) {
      const imageUrl = getImageUrl(item);
      const price = formatMoney(item.price);
      const compareAt = formatMoney(item.compare_at_price);
      const showCompare = compareAt && Number(String(item.compare_at_price).replace(/[^\d.]/g, '')) >
        Number(String(item.price).replace(/[^\d.]/g, ''));

      const imageMarkup = imageUrl
        ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(item.title)}" loading="lazy">`
        : '<div class="header-search-results__empty">No image</div>';

      return `
        <a class="header-search-product" href="${escapeHtml(item.url)}">
          <div class="header-search-product__media">${imageMarkup}</div>
          <div class="header-search-product__content">
            <span class="header-search-product__title">${escapeHtml(item.title)}</span>
            <div class="header-search-product__pricing">
              ${showCompare ? `<span class="header-search-product__mrp">${escapeHtml(compareAt)}</span>` : ''}
              ${price ? `<span class="header-search-product__price">${escapeHtml(price)}</span>` : ''}
            </div>
          </div>
        </a>
      `;
    }

    function renderGroup(items) {
      if (!items || !items.length) return '';
      return `
        <section class="header-search-group">
          <div class="header-search-products-grid">${items.map(renderProductCard).join('')}</div>
        </section>
      `;
    }

    /* --- Predictive search --- */
    async function performSearch(term) {
      const query = (term || '').trim();

      if (!query) {
        resetResults();
        return;
      }

      searchPanel?.classList.add('has-query');
      if (!results) return;

      controller?.abort();
      controller = new AbortController();

      results.setAttribute('aria-busy', 'true');
      results.innerHTML = '<div class="header-search-results__status">Searching…</div>';

      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product',
        'resources[limit]': '4',
        'resources[options][unavailable_products]': 'last'
      });

      try {
        const response = await fetch(`${predictiveUrl}.json?${params.toString()}`, {
          signal: controller.signal,
          headers: { Accept: 'application/json' }
        });

        if (!response.ok) throw new Error('Search request failed.');

        const data = await response.json();
        const products = data.resources?.results?.products || [];
        const html = renderGroup(products);

        results.innerHTML =
          html ||
          `<div class="header-search-results__empty">No products found for “${escapeHtml(query)}”.</div>`;

        if (viewAll) {
          viewAll.href = `${searchUrl}?q=${encodeURIComponent(query)}&type=product`;
          viewAll.hidden = products.length === 0;
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          results.innerHTML = '<div class="header-search-results__empty">Results could not load. Try again.</div>';
          if (viewAll) viewAll.hidden = true;
        }
      } finally {
        results.setAttribute('aria-busy', 'false');
      }
    }

    function queueSearch(value) {
      clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => performSearch(value), 250);
    }

    /* --- Events --- */
    burger?.addEventListener('click', openSidebar);
    sidebarClose?.addEventListener('click', closeSidebar);
    overlay?.addEventListener('click', closeSidebar);

    searchTriggers.forEach((trigger) => trigger.addEventListener('click', openSearch));
    closeButtons.forEach((button) => button.addEventListener('click', closeSearch));

    searchTags.forEach((tagBtn) => {
      tagBtn.addEventListener('click', () => {
        const keyword = tagBtn.getAttribute('data-search-keyword');
        if (!panelInput || !keyword) return;
        panelInput.value = keyword;
        performSearch(keyword);
        panelInput.focus();
      });
    });

    panelInput?.addEventListener('input', () => queueSearch(panelInput.value));

    searchForm?.addEventListener('submit', (event) => {
      if (!panelInput?.value.trim()) event.preventDefault();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      closeSearch();
      closeSidebar();
    });

    // close the mobile drawer when we move back up to desktop
    const desktopQuery = window.matchMedia('(min-width: 990px)');
    const handleBreakpoint = (event) => {
      if (event.matches) closeSidebar();
    };
    if (desktopQuery.addEventListener) {
      desktopQuery.addEventListener('change', handleBreakpoint);
    } else {
      desktopQuery.addListener(handleBreakpoint);
    }
  }

  function initializeAllHeaders(container = document) {
    if (container instanceof Element && container.matches('.site-header')) {
      initializeHeader(container);
    }
    const headers = container.querySelectorAll ? container.querySelectorAll('.site-header') : [];
    headers.forEach(initializeHeader);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAllHeaders());
  } else {
    initializeAllHeaders();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAllHeaders(event.target));
})();
